/**
 * Main App Component
 * Root application component with all providers and new features
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { queryClient } from '@/utils/queryClient';
import {
  useConversations,
  CONVERSATIONS_QUERY_KEY,
} from '@/hooks/useConversations';
import { AuthProvider, useAuth, msalInstance } from '@/context/AuthContext';
import { StorageProvider, useStorage } from '@/context/StorageContext';
import {
  PreferencesProvider,
  usePreferences,
} from '@/context/PreferencesContext';
import { AgentProvider, useAgent } from '@/context/AgentContext';
import { AdminProvider, useAdmin } from '@/context/AdminContext';
import {
  LoginPage,
  DatabaseLoginPage,
  PasswordChangeModal,
} from '@/components/Auth';
import { MainLayout } from '@/components/Layout/MainLayout';
import { ChatInterface } from '@/components/Chat/ChatInterface';
import { ConversationHistory } from '@/components/Chat/ConversationHistory';
import type { ProcessingStep } from '@/components/Chat/ProcessingStatus';
import { SettingsModal } from '@/components/Settings/SettingsModal';
import { HelpGuide } from '@/components/Help/HelpGuide';
import {
  loadUserContext,
  clearUserContext,
} from '@/services/userContextService';
import { AdminConfigModal } from '@/components/Admin/AdminConfigModal';
import { AgentManagement } from '@/components/Agents';
import {
  LoadingSpinner,
  SkipNavigation,
  useScreenReaderAnnouncement,
} from '@/components/Common';
import {
  searchWithRetry,
  buildContextFromSearchResults,
} from '@/services/graphService';
import {
  getChatCompletionWithRetry,
  messagesToOpenAIFormat,
  buildFileContext,
  runSecurityScan,
  toMultimodalUserContent,
} from '@/services/openAIService';
import { isUserCancellation, createCancellationError } from '@/utils/cancellation';
import { sendUsageEvent } from '@/services/usageTelemetry';
import { initErrorTelemetry, reportError } from '@/services/errorTelemetry';
import { getBackendUrl } from '@/config/runtimeConfig';
import {
  createWebSearchAdapter,
  buildContextFromWebSearchResults,
} from '@/services/webSearch';
import {
  searchAIIndex,
  buildContextFromAISearchResults,
  extractCitationsFromAISearch,
} from '@/services/azureAISearchService';
import { startChatTurn } from '@/services/chatTracing';
import { retryStorageWrite } from '@/services/storage/retryStorageWrite';
import {
  stageConversation,
  markConversationSynced,
  reconcileOutbox,
} from '@/services/conversationOutbox';
import { setCurrentTraceId } from '@/services/webSearch/webSearchUtils';
import { useWebSearchToggle } from '@/hooks/useWebSearchToggle';
import { buildEffectiveWebSearchConfig } from '@/hooks/buildEffectiveWebSearchConfig';
import { useReasoningToggle } from '@/hooks/useReasoningToggle';
import { buildEffectiveReasoningEffort } from '@/hooks/buildEffectiveReasoningEffort';
import {
  parseFiles,
  getFileType,
  isImageFile,
  imageFileToDataUrl,
  FileReadError,
  ParserModuleLoadError,
} from '@/utils/fileParser';
import { fileContentCharBudget } from '@/utils/tokenBudget';
import { generateConversationTitle } from '@/utils/formatters';
import { agentSupportsVision } from '@/utils/agentCapabilities';
import { buildUploadFileWarning } from '@/utils/uploadWarning';
import type { RetrievalSource } from '@/utils/citationInstructions';
import type { AgentWebSearchConfig } from '@/types/webSearch.types';
import type { Message, Conversation, Citation, UploadedFile } from '@/types';
import type { FileParseWarning } from '@/types/file.types';

type View = 'chat' | 'agents';

function AppContent() {
  const {
    isAuthenticated,
    isLoading: authLoading,
    user,
    isDatabaseAuthEnabled,
  } = useAuth();
  const { adapter, isInitialized: storageInitialized } = useStorage();
  const { config: adminConfig } = useAdmin();
  const { selectedAgent, isLoading: agentsLoading } = useAgent();
  // Derived deployment / model name for the selected agent. Memoized
  // so ChatInterface's `model` prop is reference-stable across the
  // input-keystroke re-renders that App.tsx fires.
  const selectedAgentModel = useMemo<string | null>(() => {
    const cfg = selectedAgent?.config?.config;
    return cfg && 'deploymentId' in cfg ? cfg.deploymentId : null;
  }, [selectedAgent]);
  const webSearchToggle = useWebSearchToggle(
    selectedAgent?.id,
    selectedAgent
  );
  const reasoningToggle = useReasoningToggle(
    selectedAgent?.id,
    selectedAgent
  );
  const { preferences } = usePreferences();
  const { announce, AnnouncementRegion } = useScreenReaderAnnouncement();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(
    null
  );
  const [processingStatus, setProcessingStatus] = useState<ProcessingStep | null>(null);
  // Holds the AbortController for the in-flight chat completion so the
  // Stop button can cancel it. Null when nothing is in flight.
  const abortControllerRef = useRef<AbortController | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string>('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);
  const [currentView, setCurrentView] = useState<View>('chat');

  // Conversations are owned by React Query — App.tsx and ConversationHistory
  // both subscribe to the same useConversations() query, replacing the old
  // pattern of parallel useState/useEffect loads coordinated by a
  // refreshTrigger prop. Mutations call queryClient.invalidateQueries to
  // refresh both views.
  const queryClient = useQueryClient();
  const { data: conversations = [] } = useConversations();

  // Ref-stash for handler dependencies (F-3). Stashing mutable state
  // here lets handler `useCallback` deps list only truly-stable values
  // (adapter, queryClient, announce). Handlers read the latest state
  // via `stateRef.current` rather than closing over stale values. The
  // post-render useEffect below keeps the ref fresh.
  const stateRef = useRef({
    messages,
    currentConversationId,
    selectedAgent,
    preferences,
    webSearchToggle,
    reasoningToggle,
    conversations,
    user,
    adminConfig,
  });

  useEffect(() => {
    stateRef.current = {
      messages,
      currentConversationId,
      selectedAgent,
      preferences,
      webSearchToggle,
      reasoningToggle,
      conversations,
      user,
      adminConfig,
    };
  });

  // Check if user needs to change password
  useEffect(() => {
    if (isAuthenticated && user?.forcePasswordChange) {
      setPasswordChangeOpen(true);
    }
  }, [isAuthenticated, user]);

  // Load the user-context the chat agent uses to personalize and recognize a
  // returning user (name, department, prior login, last activity). Fetched
  // once after sign-in; cleared on sign-out so it never leaks across users.
  useEffect(() => {
    if (isAuthenticated && user) {
      void loadUserContext(user.name, user.department);
    } else {
      clearUserContext();
    }
  }, [isAuthenticated, user]);

  // Always open with a blank conversation for privacy/UX. The user can
  // click into saved conversations from the sidebar; the React Query
  // hook above handles the actual load.
  useEffect(() => {
    if (!storageInitialized || !adapter || !user) return;
    setCurrentConversationId(uuidv4());
    setMessages([]);
  }, [storageInitialized, adapter, user]);

  // On load, replay any conversations that were staged locally but never
  // confirmed to the backend (a save aborted by a refresh, or dropped on a
  // transient blip). The backend POST is an idempotent upsert, so replaying
  // is always safe. Runs once per signed-in user; on success it refreshes the
  // sidebar so the recovered conversation reappears. Mirrors the save path's
  // `user?.id || 'local-user'` so staged and reconciled keys always match.
  const reconciledUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!storageInitialized || !adapter || !user) return;
    const uid = user.id || 'local-user';
    if (reconciledUserRef.current === uid) return;
    reconciledUserRef.current = uid;
    void reconcileOutbox(
      (conversation) => adapter.saveConversation(conversation),
      uid
    )
      .then((synced) => {
        if (synced > 0) {
          queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
        }
      })
      .catch((error) => {
        // reconcileOutbox swallows per-item failures; this is defensive only.
        console.warn('[App] Outbox reconcile failed:', error);
      });
  }, [storageInitialized, adapter, user, queryClient]);

  // Handle sending messages
  const handleSendMessage = useCallback(
    async (content: string, files?: FileList) => {
    const {
      messages,
      currentConversationId,
      selectedAgent,
      preferences,
      webSearchToggle,
      reasoningToggle,
      conversations,
      user,
      adminConfig,
    } = stateRef.current;
    const tracer = startChatTurn();
    setCurrentTraceId(tracer.traceId);
    const stopClientPrep = tracer.markStart('client_prep');

    // Fresh controller per send so any prior abort state is discarded.
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setGenerationStartedAt(Date.now());
    setProcessingStatus(null); // Clear any previous status

    try {
      // Create user message
      const userMessage: Message = {
        id: uuidv4(),
        role: 'user',
        content,
        timestamp: new Date(),
        status: 'sent',
      };

      // Add user message to state
      setMessages((prev) => [...prev, userMessage]);

      // Announce message sent for screen readers
      if (preferences.screenReaderOptimized) {
        announce('Message sent', 'polite');
      }

      // Process uploaded files if any
      let fileContext = '';
      // Accumulates pdf_truncated warnings so they can be attached to the
      // assistant message after a successful chat completion.
      const pendingFileWarnings: FileParseWarning[] = [];
      // Base64 data URLs for image attachments on THIS turn. Sent to the
      // model as image_url content parts (only when the agent supports
      // vision) and persisted in userMessage.files[].content.
      const imageDataUrls: string[] = [];
      const visionEnabled = agentSupportsVision(selectedAgent);
      if (files && files.length > 0) {
        console.log('[App] Processing files:', files.length);
        setProcessingStatus({
          step: 'analyzing',
          message: 'Processing uploaded files...',
        });

        const fileArray = Array.from(files);
        const imageFiles = fileArray.filter((f) => isImageFile(f));
        const docFiles = fileArray.filter((f) => !isImageFile(f));

        try {
          // Scale the per-file content cap (ALL types) to the agent model's
          // context window so a big-window model ingests more of a large
          // spreadsheet/document while a small window stays bounded.
          const agentWindowTokens =
            selectedAgent?.config.framework === 'azure-ai-agent'
              ? selectedAgent.config.config.contextWindowTokens
              : undefined;
          const stopFileParse = tracer.markStart('file_parse');
          const parsed = await parseFiles(docFiles, {
            maxFileSizeMB: adminConfig.uploadLimits.maxFileSizeMB,
            allowedExtensions: adminConfig.uploadLimits.allowedExtensions,
            maxContentChars: fileContentCharBudget(agentWindowTokens),
          });
          stopFileParse();
          console.log('[App] Files parsed successfully:', parsed.map(p => ({
            name: p.fileName,
            type: p.fileType,
            contentLength: p.content?.length || 0
          })));

          fileContext = buildFileContext(parsed.map((p) => p.content));
          console.log('[App] File context built, length:', fileContext.length);

          // Aggregate content-truncation warnings (PDF pages, XLSX rows, or
          // the generic char backstop) and fire fire-and-forget telemetry.
          parsed.forEach((p) => {
            p.warnings?.forEach((w) => {
              if (
                w.code === 'pdf_truncated' ||
                w.code === 'xlsx_truncated' ||
                w.code === 'content_truncated'
              ) {
                pendingFileWarnings.push(w);
                reportError('File content truncated', {
                  area: 'file-truncated',
                  code: w.code,
                  fileName: p.fileName,
                  ...(w.meta ?? {}),
                }, 'warning');
              }
            });
          });
        } catch (error) {
          console.error('[App] File parsing error:', error);
          // A FileReadError means an attached file with bytes on disk read
          // back empty even after a retry — a stale File handle after the tab
          // idled, not a parse bug. Block the send and tell the user to
          // re-attach or refresh, instead of silently shipping an empty file
          // block the model reads as "no content". Tag it distinctly on the
          // dashboard so this (previously invisible) failure is observable.
          // A ParserModuleLoadError means the code-split parser chunk
          // (pdf.js / mammoth / exceljs) failed to load — usually a deploy
          // replaced the hashed chunk while this tab stayed open. A refresh
          // pulls the new build, so we tell the user to refresh.
          const readErr = error instanceof FileReadError ? error : null;
          const moduleErr =
            error instanceof ParserModuleLoadError ? error : null;
          reportError(error, {
            area: readErr
              ? 'file-read-empty'
              : moduleErr
                ? 'file-parser-stale-chunk'
                : 'file-parse',
            fileNames: docFiles.map((f) => f.name),
            fileSizes: docFiles.map((f) => f.size),
            fileTypes: docFiles.map((f) => f.type || 'unknown'),
            effectiveMaxMB: adminConfig.uploadLimits.maxFileSizeMB,
            effectiveMaxFiles: adminConfig.uploadLimits.maxFiles,
          }, 'error');
          setProcessingStatus(null);
          if (readErr) {
            const blocked = new Error(readErr.message) as Error & {
              warning?: import('@/types').MessageWarning;
            };
            blocked.warning = {
              type: 'file_read_failed',
              title: "Couldn't read the attached file",
              message: `Couldn't read "${readErr.fileName}". This can happen when the page has been open a while — please re-attach the file (or refresh the page) and send again.`,
              actionable: [
                'Remove the file and attach it again.',
                'If that does not help, refresh the page, then re-attach.',
              ],
            };
            throw blocked;
          }
          if (moduleErr) {
            const blocked = new Error(moduleErr.message) as Error & {
              warning?: import('@/types').MessageWarning;
            };
            blocked.warning = {
              type: 'parser_unavailable',
              title: 'Refresh to finish processing this file',
              message: `Couldn't load the file reader for "${moduleErr.fileName}" — the app was likely updated while this tab stayed open. Refresh the page, then re-attach the file.`,
              actionable: ['Refresh the page, then re-attach the file.'],
            };
            throw blocked;
          }
          throw new Error(`Failed to process uploaded files: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }

        // Encode images to base64 data URLs (for the model + persistence).
        // Only for vision-capable agents — a non-vision agent would store
        // base64 it never sends, so skip encoding entirely.
        const imageDataUrlByFile = new Map<File, string>();
        if (visionEnabled && imageFiles.length > 0) {
          const stopVisionEncode = tracer.markStart('vision_encode');
          for (const imageFile of imageFiles) {
            try {
              const dataUrl = await imageFileToDataUrl(imageFile);
              imageDataUrls.push(dataUrl);
              imageDataUrlByFile.set(imageFile, dataUrl);
            } catch (error) {
              pendingFileWarnings.push({
                code: 'image_encode_failed',
                message: `Couldn't attach the image "${imageFile.name}" — it wasn't sent to the assistant.`,
              });
              reportError(error, {
                area: 'image-encode',
                fileName: imageFile.name,
                fileSize: imageFile.size,
              }, 'error');
            }
          }
          stopVisionEncode();
        }

        // Add file info to user message. Images carry their data URL in
        // `content` so reopening the conversation can re-render them.
        const uploadedFiles: UploadedFile[] = fileArray.map((file) => {
          const fileType = getFileType(file);
          return {
            id: uuidv4(),
            name: file.name,
            type: fileType === 'unknown' ? 'txt' : fileType,
            size: file.size,
            content: imageDataUrlByFile.get(file) ?? '',
            uploadedAt: new Date(),
          };
        });
        userMessage.files = uploadedFiles;
      }

      // Agent-based routing: Search M365 only for document search agent
      let context = fileContext;
      let citations: Citation[] = [];
      const activeSources: RetrievalSource[] = [];

      // ============================================
      // AZURE AI SEARCH INTEGRATION (HIGHEST PRIORITY)
      // ============================================
      // Check if selected agent has AI Search enabled
      const aiSearchConfig = selectedAgent?.aiSearchConfig;

      if (aiSearchConfig?.enabled) {
        try {
          console.log('[App] === AZURE AI SEARCH ENABLED ===');
          console.log('[App] Configuration:', {
            indexName: aiSearchConfig.indexName,
            endpoint: aiSearchConfig.endpoint,
            semanticConfig: aiSearchConfig.semanticConfig,
            useSemanticRanking: aiSearchConfig.useSemanticRanking,
            useVectorSearch: aiSearchConfig.useVectorSearch,
            top: aiSearchConfig.top || 5,
          });

          setProcessingStatus({
            step: 'searching',
            message: 'Searching indexed documents...',
            source: 'ai-search',
          });

          // Execute AI Search query
          console.log('[App] Query:', content);
          const stopAiSearch = tracer.markStart('ai_search');
          const aiSearchResponse = await searchAIIndex(aiSearchConfig, content, {
            agentId: selectedAgent?.id ?? null,
            signal: abortController.signal,
          });
          stopAiSearch();

          console.log('[App] === AI SEARCH RESULTS ===');
          console.log('[App] Summary:', {
            resultCount: aiSearchResponse.results.length,
            totalResults: aiSearchResponse.count,
          });

          // Log each result for debugging
          aiSearchResponse.results.forEach((result, index) => {
            console.log(`[App] Result ${index + 1}:`, {
              title: result.title,
              score: result.score.toFixed(4),
              url: result.url,
              hasCaption: !!result.captions && result.captions.length > 0,
              captionLength: result.captions?.join(' ').length || 0,
              contentLength: result.content?.length || 0,
              contentPreview: result.content?.substring(0, 100) + '...',
            });
          });

          // Validate semantic ranking configuration
          if (!aiSearchConfig.useSemanticRanking && !aiSearchConfig.semanticConfig) {
            console.warn('[App] ⚠️ WARNING: Semantic ranking is NOT enabled!');
            console.warn('[App] For best results, enable semantic ranking in agent configuration');
            console.warn('[App] This may result in lower quality document retrieval and no semantic captions');
          } else {
            console.log('[App] ✅ Semantic ranking enabled:', aiSearchConfig.semanticConfig);
          }

          // Build context from AI Search results
          // Using 128k character context window to match GPT-5's capabilities (128k chars ≈ 32k tokens)
          const aiSearchContext = buildContextFromAISearchResults(
            aiSearchResponse.results,
            128000
          );

          console.log('[App] === CONTEXT BUILDING ===');
          console.log('[App] Context length:', aiSearchContext.length, 'characters');
          console.log('[App] Context preview (first 500 chars):', aiSearchContext.substring(0, 500) + '...');

          // Add to existing context
          context = context + '\n\n' + aiSearchContext;

          // Extract citations for UI
          const aiSearchCitations: Citation[] = extractCitationsFromAISearch(
            aiSearchResponse.results
          ).map((result, index) => ({
            id: `ai-search-${index + 1}`,
            documentName: result.title,
            documentType: 'document',
            webUrl: result.url,
            lastModified: new Date(),
            snippet: result.snippet,
            relevanceScore: aiSearchResponse.results[index].score,
            provider: 'Azure AI Search',
          }));

          // Add to citations
          citations = [...citations, ...aiSearchCitations];

          console.log('[App] Citations extracted:', aiSearchCitations.length);
          console.log('[App] === AI SEARCH COMPLETE ===');

          if (aiSearchResponse.results.length > 0) {
            activeSources.push('aisearch');
          }
        } catch (error) {
          // A user Stop pressed mid-search surfaces here as an AbortError.
          // Cancel the whole turn instead of degrading to the next source.
          // Throw a TYPED cancellation (carries the `cancelled` warning) — a
          // raw fetch AbortError has no `.warning`, so the outer catch would
          // render the generic "something went wrong" and log it as an error.
          if (isUserCancellation(error, abortController.signal)) {
            throw createCancellationError();
          }
          console.error('[App] === AI SEARCH FAILED ===');
          console.error('[App] Error:', error);
          // Continue without AI Search context (graceful degradation)
          // Don't throw - we can fall back to Graph API or web search
        }
      }

      // ============================================
      // MICROSOFT GRAPH API SEARCH (ADDITIVE)
      // ============================================
      // Appends to any AI Search context/citations above rather than
      // replacing them, so an agent with both AI Search and M365 search
      // (and/or web search below) surfaces all sources together.
      // Check if selected agent has search capability (M365 agent)
      const shouldSearchM365 =
        selectedAgent?.capabilities?.includes('search') ||
        selectedAgent?.capabilities?.includes('document-processing');

      if (shouldSearchM365) {
        // M365 Agent: Use RAG pattern with document search
        try {
          setProcessingStatus({
            step: 'searching',
            message: 'Searching Microsoft 365...',
            source: 'graph-api',
          });

          const searchResult = await searchWithRetry(
            content,
            undefined,
            abortController.signal
          );

          if (
            searchResult.hitsContainers &&
            searchResult.hitsContainers.length > 0
          ) {
            const hits = searchResult.hitsContainers[0].hits;

            if (hits && hits.length > 0) {
              // Build context from search results. Append (don't overwrite)
              // so M365 results coexist with any AI Search context already
              // added above and any web context added below.
              const graphContext = buildContextFromSearchResults(hits);
              context = context + '\n\n' + graphContext;

              // Extract citations — merge with existing, matching the
              // append semantics of the AI Search and web blocks. Not
              // capped: the context labels every hit [M365 N], so a
              // citation row must exist for each one the model may cite.
              const m365Citations: Citation[] = hits.map((hit) => ({
                id: hit.hitId,
                documentName: hit.resource.name,
                documentType: hit.resource.file?.mimeType || 'unknown',
                webUrl: hit.resource.webUrl,
                lastModified: new Date(
                  hit.resource.lastModifiedDateTime
                ),
                snippet: hit.summary || '',
                relevanceScore: hit.rank,
              }));
              citations = [...citations, ...m365Citations];

              activeSources.push('m365');
            }
          }
        } catch (error) {
          // User Stop cancels the whole turn — don't swallow it as a
          // graceful M365 degradation. Typed cancellation so the outer catch
          // shows "You stopped the response…" (see AI Search note above).
          if (isUserCancellation(error, abortController.signal)) {
            throw createCancellationError();
          }
          console.error('M365 search failed:', error);
          // Continue without M365 context
        }
      }
      // ChatGPT Agent: Direct LLM conversation without M365 search

      // ============================================
      // WEB SEARCH INTEGRATION
      // ============================================
      // Build effective web search config from agent config + user toggle.
      // The helper returns the base `WebSearchConfig`; cast back to the
      // agent-discriminated union so downstream factory / adapter signatures
      // accept it. The agent config we pass in is a member of that union,
      // and the helper preserves the provider field, so this cast is sound.
      const effectiveWebSearchConfig = buildEffectiveWebSearchConfig(
        selectedAgent?.webSearchConfig,
        webSearchToggle.state,
        webSearchToggle.advancedAvailable
      ) as AgentWebSearchConfig | null;

      if (effectiveWebSearchConfig) {
        try {
          console.log('[App] Initializing web search:', effectiveWebSearchConfig.provider);
          setProcessingStatus({
            step: 'searching',
            message: `Searching the web (${effectiveWebSearchConfig.provider})...`,
            source: 'web-search',
          });

          // Create and initialize adapter
          const webSearchAdapter = createWebSearchAdapter(effectiveWebSearchConfig);

          if (webSearchAdapter) {
            await webSearchAdapter.initialize(effectiveWebSearchConfig);

            // Execute search with a hard timeout AND the turn's abort
            // signal. The adapter fetch now honors `signal`, so a user Stop
            // rejects this immediately (AbortError) instead of the Stop
            // looking dead until the search resolves. The timeout still
            // backstops a hung upstream call (rate limit, network, provider
            // outage) that would otherwise block the chat turn — it falls
            // through to the catch below for graceful degradation; an abort
            // is re-thrown there to cancel the whole turn.
            console.log('[App] Executing web search for:', content);
            const stopWebSearch = tracer.markStart('web_search');
            const WEB_SEARCH_TIMEOUT_MS = 20_000;
            const searchResponse = await Promise.race([
              webSearchAdapter.search(content, { signal: abortController.signal }),
              new Promise<never>((_, reject) =>
                setTimeout(
                  () => reject(new Error('Web search timed out after 20s')),
                  WEB_SEARCH_TIMEOUT_MS,
                ),
              ),
            ]);
            stopWebSearch();

            console.log('[App] Web search results:', {
              provider: searchResponse.metadata?.provider,
              resultCount: searchResponse.results.length,
              totalResults: searchResponse.totalResults,
            });

            // Build context from results
            const webContext = buildContextFromWebSearchResults(
              searchResponse.results,
              searchResponse.answer // Tavily AI summary
            );

            // Add to existing context
            context = context + '\n\n' + webContext;

            // Extract citations for UI (similar to M365 citations). Not
            // capped: the context labels every result [Web N], so a
            // citation row must exist for each one the model may cite.
            const webCitations: Citation[] = searchResponse.results
              .map((result, index) => ({
              id: `web-${index + 1}`,
              documentName: result.title,
              documentType: 'web',
              webUrl: result.url,
              lastModified: result.publishedDate || new Date(),
              snippet: result.snippet,
              relevanceScore: result.relevanceScore,
              provider: searchResponse.metadata?.provider || effectiveWebSearchConfig.provider,
            }));

            // Merge with existing citations
            citations = [...citations, ...webCitations];

            console.log('[App] Web search context added:', {
              contextLength: webContext.length,
              citationsCount: webCitations.length,
            });

            if (searchResponse.results.length > 0) {
              activeSources.push('web');
            }
          }
        } catch (error) {
          // User Stop cancels the whole turn. Throw a TYPED cancellation
          // BEFORE reportError so a self-inflicted cancel both renders
          // "You stopped the response…" and is skipped by the outer catch's
          // `wasCancelled` guard (no dashboard Errors pollution).
          if (isUserCancellation(error, abortController.signal)) {
            throw createCancellationError();
          }
          console.error('[App] Web search failed:', error);
          // Report so ops sees the failure even though we degrade
          // gracefully — provider outages and rate-limit storms were
          // invisible on the dashboard before this.
          reportError(error, {
            area: 'web-search',
            provider: effectiveWebSearchConfig.provider,
            agentId: selectedAgent?.id,
            traceId: tracer.traceId,
          }, 'warning');
          // Continue without web search context (graceful degradation)
          // Don't throw - we can still respond using LLM knowledge
        }
      }

      // Extract agent-specific Azure AI configuration
      let agentConfig;
      if (selectedAgent?.config.framework === 'azure-ai-agent') {
        const config = selectedAgent.config.config;
        agentConfig = {
          endpoint: config.endpoint,
          apiKey: config.apiKey,
          deployment: config.deploymentId,
          apiVersion: config.apiVersion,
        };
        console.log('[App] Using agent config:', {
          agentName: selectedAgent.name,
          endpoint: agentConfig.endpoint,
          deployment: agentConfig.deployment,
          apiVersion: agentConfig.apiVersion,
          hasApiKey: !!agentConfig.apiKey,
        });

        // Warn if critical config is missing
        if (!agentConfig.endpoint || !agentConfig.deployment) {
          console.warn('[App] WARNING: Agent config incomplete!', {
            hasEndpoint: !!agentConfig.endpoint,
            hasDeployment: !!agentConfig.deployment,
            hasApiKey: !!agentConfig.apiKey,
          });
        }
      } else {
        console.log('[App] No agent config, using defaults');
      }

      // Get AI response
      setProcessingStatus({
        step: 'generating',
        message: 'Generating response...',
      });

      const stopContextBuild = tracer.markStart('context_build');
      const conversationHistory = messagesToOpenAIFormat([
        ...messages,
        userMessage,
      ]);

      // Approach A: only the current user turn becomes multimodal, and only
      // when images were attached to a vision-capable agent. History stays
      // string-only. Defense-in-depth: the composer already hides image
      // upload for non-vision agents.
      if (visionEnabled && imageDataUrls.length > 0) {
        const last = conversationHistory[conversationHistory.length - 1];
        if (last && last.role === 'user' && typeof last.content === 'string') {
          last.content = toMultimodalUserContent(last.content, imageDataUrls);
        }
      }
      stopContextBuild();

      // Enhanced logging for debugging empty response issue
      console.log('[App] Preparing LLM request:', {
        contextLength: context.length,
        fileContextLength: fileContext.length,
        messageCount: conversationHistory.length,
        hasAgentConfig: !!agentConfig,
        citationsCount: citations.length,
      });

      // Detect-only security scan — its `securityFlag` is attached to
      // the assistant message bubble but is not on the critical path of
      // the LLM call. Kick it off in parallel with the completion so
      // up to 1.5s of scan latency overlaps the LLM wait instead of
      // serializing into `client_prep`. runSecurityScan swallows its
      // own errors and returns `securityFlag: false` on timeout, so the
      // chat user never pays a penalty for a slow / down scan endpoint.
      const securityScanPromise = runSecurityScan({
        prompt: content,
        conversationId: currentConversationId || null,
        agentId: selectedAgent?.id ?? null,
        messageId: userMessage.id,
      });

      // client_prep covers everything from start of turn through retrieval and
      // pre-LLM build-up; stop it now just before the LLM call begins.
      stopClientPrep();

      // Resolve the per-turn "advanced thinking" effort. Returns null
      // (→ omit the parameter) unless the agent is reasoning-capable and
      // the per-turn toggle is on.
      const reasoningConfig =
        selectedAgent?.config.framework === 'azure-ai-agent'
          ? selectedAgent.config.config.reasoningConfig
          : undefined;
      const reasoningEffort = buildEffectiveReasoningEffort(
        reasoningConfig,
        reasoningToggle.state
      );

      // Agent-declared input context window, used to budget the request
      // (trim history + cap context) before it is sent. Undefined falls back
      // to the conservative default inside the OpenAI service.
      const contextWindowTokens =
        selectedAgent?.config.framework === 'azure-ai-agent'
          ? selectedAgent.config.config.contextWindowTokens
          : undefined;
      const contextOverflowStrategy =
        selectedAgent?.config.framework === 'azure-ai-agent'
          ? selectedAgent.config.config.contextOverflowStrategy
          : undefined;
      const summaryModel =
        selectedAgent?.config.framework === 'azure-ai-agent'
          ? selectedAgent.config.config.summaryModel
          : undefined;

      const completionStart = Date.now();
      const [enhancedResponse, securityScan] = await Promise.all([
        getChatCompletionWithRetry(
          conversationHistory,
          context,
          agentConfig,
          {
            temperature: preferences.temperature,
            maxTokens: preferences.maxTokens,
            ...(reasoningEffort ? { reasoningEffort } : {}),
            ...(contextWindowTokens
              ? { inputTokenLimit: contextWindowTokens }
              : {}),
            ...(contextOverflowStrategy
              ? { contextOverflowStrategy }
              : {}),
            ...(summaryModel?.deployment ? { summaryModel } : {}),
          },
          3, // maxRetries (default value)
          selectedAgent?.systemPrompt, // Use agent's custom system prompt
          tracer,
          activeSources,
          abortController.signal
        ),
        securityScanPromise,
      ]);
      const completionLatencyMs = Date.now() - completionStart;

      // Debug logging to verify response content
      console.log('[App] AI response received:', {
        length: enhancedResponse.content?.length || 0,
        preview: enhancedResponse.content?.substring(0, 100) || '(empty)',
        hasContent: !!enhancedResponse.content && enhancedResponse.content.trim().length > 0,
        finishReason: enhancedResponse.finishReason,
        tokenUsage: enhancedResponse.usage,
        hasWarning: !!enhancedResponse.warning,
      });

      // Build the final warning for the assistant message.
      // If any uploaded files were truncated, prepend that notice.
      // If the API response also has a warning (e.g. token truncation),
      // merge both messages so neither is silently dropped.
      const apiWarning = enhancedResponse.warning;
      const assistantWarning = buildUploadFileWarning(
        pendingFileWarnings,
        apiWarning,
      );

      // Create assistant message with enhanced metadata
      const assistantMessage: Message = {
        id: uuidv4(),
        role: 'assistant',
        content: enhancedResponse.content,
        timestamp: new Date(),
        citations: citations.length > 0 ? citations : undefined,
        tokenUsage: enhancedResponse.usage,      // NEW: Token usage statistics
        warning: assistantWarning,               // NEW: Warning if present (file truncation or API warning)
        // Transient — drives the inline security-flag banner on the
        // assistant bubble. Storage adapters drop unknown fields, so
        // this naturally doesn't survive a reload, which is the
        // intended behaviour (the persistent audit log lives on the
        // dashboard Security page).
        securityFlag: securityScan.securityFlag || undefined,
      };

      // Fire-and-forget analytics beacon — swallowed errors inside the service
      if (enhancedResponse.usage) {
        const agentConfigPayload = selectedAgent?.config?.config;
        const model = agentConfigPayload && 'deploymentId' in agentConfigPayload
          ? agentConfigPayload.deploymentId
          : null;
        void sendUsageEvent({
          agentId: selectedAgent?.id ?? null,
          conversationId: currentConversationId || null,
          messageId: assistantMessage.id,
          model,
          servedModel: enhancedResponse.servedModel ?? null,
          promptTokens: enhancedResponse.usage.promptTokens,
          completionTokens: enhancedResponse.usage.completionTokens,
          finishReason: enhancedResponse.finishReason ?? null,
          latencyMs: completionLatencyMs,
          traceId: tracer.traceId,
          callType: 'chat',
        });
      }

      // Attribute the auxiliary summarization call (when the 'summarize'
      // overflow strategy fired) to the same user/conversation/message so
      // per-user/department/conversation cost stays accurate. Tagged
      // callType:'summarize' + the summary model so cost rolls up at the
      // (cheaper) summary rate without skewing request-count / quality
      // metrics. Fire-and-forget, same as the main beacon.
      if (enhancedResponse.summaryUsage) {
        const su = enhancedResponse.summaryUsage;
        void sendUsageEvent({
          agentId: selectedAgent?.id ?? null,
          conversationId: currentConversationId || null,
          messageId: assistantMessage.id,
          model: su.model,
          servedModel: su.servedModel ?? null,
          promptTokens: su.usage.promptTokens,
          completionTokens: su.usage.completionTokens,
          finishReason: 'stop',
          latencyMs: su.latencyMs,
          traceId: tracer.traceId,
          callType: 'summarize',
        });
      }

      console.log('[App] Assistant message created:', {
        id: assistantMessage.id,
        contentLength: assistantMessage.content?.length || 0,
        hasCitations: !!assistantMessage.citations,
        citationCount: assistantMessage.citations?.length || 0,
      });

      // Add assistant message to state
      const newMessages = [...messages, userMessage, assistantMessage];
      const stopClientRender = tracer.markStart('client_render');
      setMessages(newMessages);
      stopClientRender();

      console.log('[App] Messages state updated:', {
        totalMessages: newMessages.length,
        lastMessageRole: newMessages[newMessages.length - 1]?.role,
        lastMessageContentLength: newMessages[newMessages.length - 1]?.content?.length || 0,
      });

      // Announce response received for screen readers
      if (preferences.screenReaderOptimized) {
        announce('Response received from AI assistant', 'polite');
      }

      // Save conversation
      const existingConv = conversations.find(
        (c) => c.id === currentConversationId
      );

      // Generate title from first user message if this is a new conversation
      let conversationTitle = existingConv?.title;
      if (!conversationTitle) {
        const firstUserMessage = newMessages.find((m) => m.role === 'user');
        conversationTitle = firstUserMessage
          ? generateConversationTitle(firstUserMessage.content)
          : 'New Conversation';
      }

      const conversation: Conversation = {
        id: currentConversationId,
        title: conversationTitle,
        messages: newMessages,
        messageCount: newMessages.length,
        createdAt: existingConv?.createdAt || new Date(),
        updatedAt: new Date(),
        agentId: selectedAgent?.id,
        userId: user?.id || 'local-user', // Associate with current user
      };

      // Persist the conversation in the background — the user already sees
      // the assistant reply, so we don't make them wait on the network
      // round-trip to the backend before clearing the spinner.
      let savePromise: Promise<unknown> = Promise.resolve();
      if (preferences.saveHistory) {
        // Durably stage the conversation locally BEFORE the network write.
        // The backend POST runs in the background and is aborted by a
        // refresh and silent on a transient blip — staging means a
        // refresh/outage can't lose the conversation; reconcileOutbox()
        // replays it on the next load. Cleared on confirmed save below.
        stageConversation(conversation);

        console.log('[App] Saving conversation:', {
          id: conversation.id,
          title: conversation.title,
          messageCount: conversation.messages.length,
          agentId: conversation.agentId,
          selectedAgentId: selectedAgent?.id,
          selectedAgentName: selectedAgent?.name,
          isExisting: !!existingConv,
        });

        const stopSaveConversation = tracer.markStart('save_conversation');
        // Bounded retry (3 attempts, 1s/3s backoff) so a transient blip
        // doesn't silently drop the persist. 4xx errors short-circuit.
        //
        // Persistence strategy:
        // - NEW conversation: POST /conversations creates the row with
        //   the initial [userMessage, assistantMessage] pair.
        // - EXISTING conversation: append the user and assistant
        //   messages individually via POST /:id/messages. Each append
        //   is bounded (~10 KB) and atomic in Postgres (JSONB `||` op),
        //   so a transient failure on the CURRENT turn cannot wipe out
        //   previously-saved turns. With the old full-array PUT, a
        //   413/network error left the row holding whatever was last
        //   successfully saved AND silently dropped the new turn.
        //
        // If the user-message append succeeds but the assistant-message
        // append fails after retries, the user's question is durable on
        // the server while the assistant response is lost from the
        // saved transcript. That's degraded but recoverable — far
        // better than losing the entire turn (or whole conversation).
        const rawSave = existingConv
          ? retryStorageWrite(() =>
              adapter.addMessage(conversation.id, userMessage)
            ).then(() =>
              retryStorageWrite(() =>
                adapter.addMessage(conversation.id, assistantMessage)
              )
            )
          : retryStorageWrite(() => adapter.saveConversation(conversation));

        savePromise = rawSave
          .then(() => {
            stopSaveConversation();
            // Backend write confirmed — drop the durable local copy so the
            // next load's reconcile doesn't redundantly replay it.
            markConversationSynced(conversation.id);
            // Optimistically write the conversation into the React Query
            // cache so the sidebar reflects the save *immediately*, even
            // if the user has already navigated to another conversation
            // (or clicked "New Conversation") by the time the save
            // resolves. The subsequent invalidateQueries reconciles
            // against the server.
            //
            // We have to mutate every cached `['conversations', ...]`
            // entry rather than only the current user's because the
            // userId suffix is appended inside the useConversations
            // hook and isn't directly available here. In practice
            // there is only ever one such entry (per signed-in user).
            queryClient.setQueriesData<Conversation[]>(
              { queryKey: CONVERSATIONS_QUERY_KEY },
              (old) => {
                if (!Array.isArray(old)) return old;
                const filtered = old.filter((c) => c.id !== conversation.id);
                return [conversation, ...filtered];
              }
            );
            // Refresh both App.tsx's and ConversationHistory's view of the
            // list — they share the same React Query cache.
            queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
          })
          .catch((saveError) => {
            stopSaveConversation();
            // Deliberately do NOT markConversationSynced here: the conversation
            // stays staged in the outbox so reconcileOutbox() replays it on the
            // next load. This is what stops a failed/aborted save from silently
            // losing the conversation.
            console.error('[App] Failed to persist conversation:', saveError);
            // Surface the failure on the dashboard Errors page so ops can
            // see when persists are silently dropping. The retry has
            // already exhausted by the time we reach this branch.
            reportError(saveError, {
              area: 'conversation-save',
              conversationId: conversation.id,
              isExisting: !!existingConv,
              traceId: tracer.traceId,
            });
          });
      } else {
        console.log('[App] Skipping conversation save (saveHistory disabled)');
      }

      // Clear processing status on success — don't block on the save.
      setProcessingStatus(null);

      // Ship the trace once the save lands so `save_conversation` timing is
      // captured in the segments. tracer.end() is itself fire-and-forget.
      void savePromise.then(() =>
        tracer.end({
          agentId: selectedAgent?.id,
          conversationId: currentConversationId || undefined,
        })
      );
    } catch (error) {
      console.error('Error sending message:', error);

      // Extract structured warning if available
      const err = error as Error & { warning?: import('@/types').MessageWarning };
      const warning = err.warning;
      const wasCancelled = warning?.type === 'cancelled';

      // Cancels are user-initiated, not failures — don't poison the
      // trace's had_error flag and don't bloat the dashboard Errors
      // page with operator self-inflicted Stops.
      if (!wasCancelled) {
        tracer.setError();
      }

      // Clear processing status on error
      setProcessingStatus(null);

      // Create error message with structured warning
      const errorMessage: Message = {
        id: uuidv4(),
        role: 'system',
        content: warning
          ? warning.message
          : 'Sorry, something went wrong. Please try again.',
        timestamp: new Date(),
        warning: warning,  // NEW: Attach structured warning for display
      };

      // Log full error for debugging
      console.error('[App] Full error details:', {
        message: err.message,
        name: err.name,
        stack: err.stack,
        warning: warning,
      });

      // Surface the failure on the dashboard Errors page so ops can
      // diagnose recurring chat-send failures (the trace alone only
      // shows `had_error=true` without a message or stack). Mirrors
      // the pattern used by the conversation-save catch below.
      if (!wasCancelled) {
        reportError(error, {
          area: 'chat-send',
          traceId: tracer.traceId,
          agentId: selectedAgent?.id,
          conversationId: currentConversationId || undefined,
        });
      }

      setMessages((prev) => [...prev, errorMessage]);

      // On the error path the save was never kicked off, so ship the trace
      // immediately rather than waiting on the (non-existent) save promise.
      void tracer.end({
        agentId: selectedAgent?.id,
        conversationId: currentConversationId || undefined,
      });
    } finally {
      setCurrentTraceId(undefined);
      setIsLoading(false);
      setGenerationStartedAt(null);
      // Only clear the ref if it's still the controller we created — a
      // rapid re-send could have already replaced it.
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
    },
    [adapter, queryClient, announce]
  );

  // Handle conversation selection. List endpoint no longer ships
  // `messages` — we must fetch the detail record before populating
  // the chat view. Failures here used to silently leave the chat
  // pane blank, which masked real bugs (e.g. a user reported being
  // unable to open conversations they'd just moved into a folder).
  // We now surface every failure mode: a 404 from the backend, an
  // empty messages array when the sidebar advertised a non-zero
  // count, and any thrown error — each gets a reportError beacon
  // plus a user-visible system message so the chat doesn't look
  // mysteriously empty.
  const handleSelectConversation = useCallback(
    async (conversation: Conversation) => {
      setCurrentConversationId(conversation.id);
      setSidebarOpen(false);
      try {
        const full = await adapter.loadConversation(conversation.id);
        if (!full) {
          reportError('Conversation load returned null', {
            area: 'conversation-load',
            conversationId: conversation.id,
            folderId: conversation.folderId,
            sidebarMessageCount: conversation.messageCount,
          }, 'error');
          setMessages([
            {
              id: uuidv4(),
              role: 'system',
              content:
                "Couldn't load this conversation. It may have been deleted or your session expired. Try refreshing the page.",
              timestamp: new Date(),
            },
          ]);
          return;
        }
        const loadedCount = full.messages?.length ?? 0;
        // Sidebar advertises message_count from the list endpoint. When the
        // detail fetch returns zero messages but the sidebar said there
        // should be some, something is wrong on the server side (corrupted
        // JSONB, partial write, etc.). Flag it so we can see it on the
        // Errors dashboard instead of presenting an empty chat.
        if (loadedCount === 0 && (conversation.messageCount ?? 0) > 0) {
          reportError('Conversation loaded with zero messages despite sidebar count', {
            area: 'conversation-load',
            conversationId: conversation.id,
            folderId: conversation.folderId,
            sidebarMessageCount: conversation.messageCount,
            loadedMessageCount: loadedCount,
          }, 'error');
          setMessages([
            {
              id: uuidv4(),
              role: 'system',
              content:
                "This conversation appears to be empty on the server, even though the sidebar lists messages for it. The issue has been logged.",
              timestamp: new Date(),
            },
          ]);
          return;
        }
        setMessages(full.messages ?? []);
      } catch (error) {
        console.error('[App] Failed to load conversation detail:', error);
        reportError(error, {
          area: 'conversation-load',
          conversationId: conversation.id,
          folderId: conversation.folderId,
          sidebarMessageCount: conversation.messageCount,
        }, 'error');
        setMessages([
          {
            id: uuidv4(),
            role: 'system',
            content:
              "Couldn't load this conversation — the server returned an error. Try again, or refresh the page.",
            timestamp: new Date(),
          },
        ]);
      }
    },
    [adapter]
  );

  // Handle new conversation
  const handleNewConversation = useCallback(() => {
    const newId = uuidv4();
    setCurrentConversationId(newId);
    setMessages([]);
    setSidebarOpen(false);
  }, []);

  // Cancel the in-flight chat completion. The catch path in
  // handleSendMessage will see AbortError, classify it as 'cancelled',
  // and render the friendly system message — finally will clear the
  // spinner and the ref.
  const handleCancelGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  // Handle delete conversation
  const handleDeleteConversation = useCallback(
    async (id: string) => {
      const { currentConversationId } = stateRef.current;
      try {
        await adapter.deleteConversation(id);
        queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });

        // If deleted current conversation, always create a new blank one
        // (don't auto-load another conversation for privacy/UX).
        if (id === currentConversationId) {
          handleNewConversation();
        }
      } catch (error) {
        console.error('Failed to delete conversation:', error);
      }
    },
    [adapter, queryClient, handleNewConversation]
  );

  // Handle file upload
  const handleFileUpload = useCallback((files: FileList) => {
    console.log('Files selected:', files.length);
    // Files will be processed when message is sent
  }, []);

  // Handle feedback toggle on a message — persists the new feedback
  // state on the message via the storage adapter so it survives reloads.
  // The thumbs handler in <Message/> already fires the analytics
  // beacon; this handler only mirrors the rating onto the message.
  const handleFeedbackChange = useCallback(
    async (
      messageId: string,
      feedback: 'up' | 'down' | null
    ): Promise<void> => {
      const { preferences, currentConversationId, conversations } =
        stateRef.current;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, feedback } : m
        )
      );

      if (!preferences.saveHistory || !currentConversationId) return;

      try {
        const existingConv = conversations.find(
          (c) => c.id === currentConversationId
        );
        if (!existingConv) return;
        const updatedMessages = existingConv.messages.map((m) =>
          m.id === messageId ? { ...m, feedback } : m
        );
        await adapter.updateConversation(currentConversationId, {
          messages: updatedMessages,
        });
        queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
      } catch (error) {
        console.warn('[App] Failed to persist feedback change:', error);
      }
    },
    [adapter, queryClient]
  );

  // Show loading screen while auth and agents initialize
  // CRITICAL: Must wait for BOTH auth AND agents before showing main app
  if (authLoading || (isAuthenticated && agentsLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" label="Initializing..." />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    // Prioritize database auth if enabled, otherwise show Entra ID login
    if (isDatabaseAuthEnabled) {
      return <DatabaseLoginPage />;
    }
    return <LoginPage />;
  }

  // Show main app with sidebar and settings
  return (
    <>
      <SkipNavigation />
      <AnnouncementRegion />
      <MainLayout
        user={user}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenHelp={() => setHelpOpen(true)}
        onOpenAdmin={() => setAdminOpen(true)}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        onNavigateToAgents={() => setCurrentView('agents')}
        onNavigateToChat={() => setCurrentView('chat')}
        currentView={currentView}
        sidebar={
          currentView === 'chat' ? (
            <ConversationHistory
              currentConversationId={currentConversationId}
              onSelectConversation={handleSelectConversation}
              onNewConversation={handleNewConversation}
              onDeleteConversation={handleDeleteConversation}
            />
          ) : null
        }
      >
        {currentView === 'chat' ? (
          <ChatInterface
            messages={messages}
            onSendMessage={handleSendMessage}
            onFileUpload={handleFileUpload}
            isLoading={isLoading}
            generationStartedAt={generationStartedAt}
            onCancelGeneration={handleCancelGeneration}
            processingStatus={processingStatus}
            conversationId={currentConversationId}
            agentId={selectedAgent?.id ?? null}
            model={selectedAgentModel}
            onFeedbackChange={handleFeedbackChange}
            onOpenGuide={() => setHelpOpen(true)}
          />
        ) : (
          <AgentManagement />
        )}
      </MainLayout>

      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      <HelpGuide isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      <AdminConfigModal
        isOpen={adminOpen}
        onClose={() => setAdminOpen(false)}
      />

      <PasswordChangeModal
        isOpen={passwordChangeOpen}
        onClose={() => setPasswordChangeOpen(false)}
        isForced={user?.forcePasswordChange ?? false}
      />
    </>
  );
}

export default function App() {
  useEffect(() => {
    // Initialize global error telemetry. Safe to call once at app startup.
    // Pass the absolute backend URL — otherwise the default `/api` is
    // relative to the SPA's origin (`stellargpt.net` in prod), which is
    // a different host from the backend. sendBeacon would silently 404
    // (or hit the SPA fallback) and the dashboard would stay empty
    // even though reportError was firing. The storage adapter has
    // always used getBackendUrl(); errorTelemetry needs the same.
    initErrorTelemetry('chat-ui', getBackendUrl());
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
     <QueryClientProvider client={queryClient}>
      {/* AuthProvider sits above AdminProvider now: AuthContext fetches its
          own config from /api/public-config, so AdminProvider no longer has
          to mount above just to seed localStorage for AuthContext. The new
          order also lets AdminContext useAuth() directly (replacing the
          auth:ready window event we used as a workaround for the inverted
          order). */}
      <AuthProvider>
        <AdminProvider>
          <PreferencesProvider>
            <StorageProvider>
              <AgentProvider>
                <AppContent />
              </AgentProvider>
            </StorageProvider>
          </PreferencesProvider>
        </AdminProvider>
      </AuthProvider>
     </QueryClientProvider>
    </MsalProvider>
  );
}
